import type { OutcomeCondition } from "@repo/app-types/entities/filterConditions";
import {
  getOutcomeDefinitionFromPlaybook,
  type OutcomeDefinition,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useMemo } from "react";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { OutcomeResultPill } from "../teammatePerformance/outcomeValuePill.js";
import { RemoveFilterChip } from "./filterChip.js";

export interface OutcomeFilterItemProps {
  playbookId: PlaybookId | undefined;
  value: OutcomeCondition | null;
  onChange: (newValue: OutcomeCondition | null) => void;
}

export const OutcomeFilterItem: FC<OutcomeFilterItemProps> = (props) => {
  const { data: minimalPlaybooks } = useAllMinimalPlaybooksQuery();
  const selectedPlaybook = useMemo(
    () => minimalPlaybooks?.find((p) => p.id === props.playbookId),
    [minimalPlaybooks, props.playbookId],
  );
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(selectedPlaybook);

  // Rendered even when the outcome definition can't be resolved — deleted from
  // the playbook, or not loaded yet. The condition carries the labels it was
  // built with, so the chip still reads correctly, and a playbook that saved
  // this outcome as its default needs a chip to take it back off.
  if (props.value === null || props.value.playbookId !== props.playbookId) {
    return null;
  }

  return (
    <RemoveFilterChip onRemove={() => props.onChange(null)}>
      {renderOutcomeCondition(outcomeDefinition, props.value)}
    </RemoveFilterChip>
  );
};

function renderOutcomeCondition(
  outcomeDefinition: OutcomeDefinition | undefined,
  condition: OutcomeCondition,
) {
  // Find the result in the outcome definition to get the category
  const result = outcomeDefinition?.results.find(
    (r) => r.value === condition.outcomeValue,
  );

  return (
    <>
      <span className="max-w-32 truncate">
        {outcomeDefinition?.name ?? "Outcome"}
      </span>
      <span className="flex items-center gap-1">
        &nbsp;is{" "}
        {result ? (
          <OutcomeResultPill size={Size.SMALL} result={result} />
        ) : (
          condition.outcomeLabel
        )}
      </span>
    </>
  );
}
